import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight, Sun, Moon, TrendingUp, Wrench, Users, BookOpen,
  HeadphonesIcon, Package, Brain, BarChart3,
} from "lucide-react";

const STEPS = [
  {
    module: "SalesFlow",
    link: "/space/rogue/sales-flow",
    icon: TrendingUp,
    description: "The sales team identifies potential customers through industry events, referrals, and online enquiries. Each suspect is logged in SalesFlow with Cerebrum automatically researching the company — pulling in 30+ data points before the first call.",
    screenshots: {
      dark: "/screenshots/dark-suspects.png",
      light: "/screenshots/light-leads.png",
    },
  },
  {
    module: "SalesFlow",
    link: "/space/rogue/sales-flow",
    icon: TrendingUp,
    title: "Leads are nurtured through the funnel",
    description: "Qualified suspects become leads. The sales team tracks every interaction, schedules follow-ups, and uses the sales funnel to visualise where each deal stands. Cerebrum drafts follow-up emails and flags leads going cold.",
    screenshots: {
      dark: "/screenshots/dark-salesfunnel.png",
      light: "/screenshots/light-salesfunnel.png",
    },
  },
  {
    module: "Ops",
    link: "/space/rogue/ops",
    icon: Wrench,
    title: "Jobs are scheduled and dispatched",
    description: "Once a customer signs, the ops team creates job cards and schedules technicians. The drag-and-drop scheduler balances workloads, and field techs get everything on their phone — routes, equipment lists, and customer history.",
    screenshots: {
      dark: "/screenshots/dark-jobcards.png",
      light: "/screenshots/light-jobcards.png",
    },
  },
  {
    module: "Ops",
    link: "/space/rogue/ops",
    icon: Wrench,
    title: "Field work is captured on site",
    description: "Technicians capture photos, notes, and customer signatures directly on the job card. GPS tracks arrival and departure. The office sees real-time progress without a single phone call.",
    screenshots: {
      dark: "/screenshots/dark-gallery.png",
      light: "/screenshots/light-gallery.png",
    },
  },
  {
    module: "Teams",
    link: "/space/rogue/teams",
    icon: Users,
    title: "The team stays connected and accountable",
    description: "Time and attendance tracks who's clocked in. Tasks are assigned and tracked. Team chat keeps conversations contextual — no more lost WhatsApp messages. Leaderboards drive healthy competition.",
    screenshots: {
      dark: "/screenshots/dark-tasks.png",
      light: "/screenshots/light-tasks.png",
    },
  },
  {
    module: "Bookkeep",
    link: "/space/rogue/bookkeep",
    icon: BookOpen,
    title: "Projects are tracked to the rand",
    description: "Every project links to its job cards, expenses, and invoices. The team sees budget vs. actual in real time. The timeline view manages dependencies across multi-week installations. Profitability is calculated before the project closes.",
    screenshots: {
      dark: "/screenshots/dark-timeline.png",
      light: "/screenshots/light-timeline.png",
    },
  },
  {
    module: "Bookkeep",
    link: "/space/rogue/bookkeep",
    icon: BookOpen,
    title: "Analytics reveal the full picture",
    description: "Revenue by customer, margin by project, conversion by rep — the analytics dashboard explains the business in plain language. Cerebrum annotates trends and flags anomalies the team would normally miss.",
    screenshots: {
      dark: "/screenshots/dark-analytics.png",
      light: "/screenshots/light-analytics.png",
    },
  },
  {
    module: "Support",
    link: "/space/rogue/support",
    icon: HeadphonesIcon,
    title: "Customers get a portal and fast support",
    description: "Customers log in to their portal to see job progress, submit tickets, and view invoices. Support tickets are tracked with SLAs. Cerebrum drafts the first reply to every ticket, cutting response times in half.",
    screenshots: {
      dark: "/screenshots/dark-tickets.png",
      light: "/screenshots/light-tickets.png",
    },
  },
];

export default function CaseStudy({ onTrialClick }) {
  const [theme, setTheme] = useState("dark");

  return (
    <div className="min-h-screen" data-testid="case-study-page">
      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-10 md:pb-14">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-2 text-sm text-geminii-dim mb-6">
              <Link to="/space/rogue" className="hover:text-white transition-colors">Rogue</Link>
              <span>/</span>
              <span className="text-rogue-cyan">Case Study</span>
            </div>
            <span className="inline-block text-[10px] font-mono text-rogue-cyan bg-rogue-cyan/10 px-2 py-1 rounded mb-4 uppercase tracking-wider">
              Interactive Case Study
            </span>
            <h1 className="font-display font-bold text-4xl sm:text-5xl text-white leading-tight mb-4">
              How one business runs everything on Rogue
            </h1>
            <p className="text-lg text-geminii-muted max-w-2xl mb-4 leading-relaxed">
              Follow the journey of a real business — from first customer contact to project delivery, team management, and beyond. Every step powered by a single platform.
            </p>
            <p className="text-sm text-geminii-dim max-w-xl mb-8">
              This walkthrough uses real demo data to show how Rogue's modules connect into one seamless operation.
            </p>

            {/* Theme toggle */}
            <div className="flex items-center gap-3 mb-6">
              <span className="text-xs text-geminii-dim">View in:</span>
              <button
                onClick={() => setTheme("dark")}
                data-testid="case-study-dark-toggle"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  theme === "dark"
                    ? "bg-white/[0.08] text-white border border-white/[0.15]"
                    : "text-geminii-dim hover:text-white border border-transparent"
                }`}
              >
                <Moon size={12} /> Dark mode
              </button>
              <button
                onClick={() => setTheme("light")}
                data-testid="case-study-light-toggle"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  theme === "light"
                    ? "bg-rogue-amber/10 text-rogue-amber border border-rogue-amber/20"
                    : "text-geminii-dim hover:text-white border border-transparent"
                }`}
              >
                <Sun size={12} /> Light mode
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Journey Steps */}
      <section className="pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="space-y-16 md:space-y-24">
              {STEPS.map((step, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5 }}
                  className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center"
                >
                  {/* Text */}
                  <div className={i % 2 === 1 ? "lg:order-2" : ""}>
                    <div className="flex items-center gap-3 mb-4">
                      {/* Step number */}
                      <div className="relative z-10 w-12 h-12 md:w-16 md:h-16 rounded-full bg-geminii-card border border-rogue-cyan/20 flex items-center justify-center flex-shrink-0">
                        <span className="font-display font-bold text-rogue-cyan text-sm md:text-base">{i + 1}</span>
                      </div>
                      <div>
                        <Link
                          to={step.link}
                          className="text-[10px] font-mono text-rogue-cyan bg-rogue-cyan/10 px-2 py-0.5 rounded hover:bg-rogue-cyan/20 transition-colors"
                        >
                          {step.module} →
                        </Link>
                      </div>
                    </div>
                    <h3 className="font-display font-bold text-xl md:text-2xl text-white mb-3 leading-tight">
                      {step.title}
                    </h3>
                    <p className="text-geminii-muted leading-relaxed">
                      {step.description}
                    </p>
                  </div>

                  {/* Screenshot */}
                  <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                    <div className="rounded-xl overflow-hidden border border-white/[0.08] shadow-2xl shadow-black/40">
                      <div className="bg-geminii-card flex items-center gap-2 px-3 py-2 border-b border-white/[0.06]">
                        <div className="flex gap-1">
                          <div className="w-2 h-2 rounded-full bg-rogue-rose/50" />
                          <div className="w-2 h-2 rounded-full bg-rogue-amber/50" />
                          <div className="w-2 h-2 rounded-full bg-rogue-emerald/50" />
                        </div>
                        <span className="text-[9px] font-mono text-geminii-dim ml-1">rogue.geminii-ai.com</span>
                        {theme === "light" && (
                          <span className="ml-auto text-[9px] font-mono text-rogue-amber/50 flex items-center gap-1">
                            <Sun size={9} /> Light
                          </span>
                        )}
                      </div>
                      <img
                        src={step.screenshots[theme]}
                        alt={step.title}
                        className="w-full"
                      />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
        </div>
      </section>

      {/* Results / Impact */}
      <section className="py-12 md:py-16 border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-12">The result</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { stat: "1 platform", desc: "replaced 4 separate tools" },
              { stat: "100%", desc: "field team on mobile" },
              { stat: "Real-time", desc: "visibility across every department" },
              { stat: "Cerebrum AI", desc: "drafting, researching, and alerting 24/7" },
            ].map((item) => (
              <div key={item.stat} className="bg-geminii-card border border-white/[0.06] rounded-xl p-6">
                <p className="font-display font-bold text-2xl text-rogue-cyan mb-2">{item.stat}</p>
                <p className="text-sm text-geminii-muted">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-16 border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-4">
            Ready to run your operation on Rogue?
          </h2>
          <p className="text-geminii-muted mb-8 max-w-md mx-auto">
            Start with one module or take the whole platform. No credit card required.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={onTrialClick}
              data-testid="case-study-cta"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-rogue-cyan text-geminii-deep font-semibold text-sm hover:shadow-[0_0_24px_rgba(31,182,193,0.35)] transition-all duration-250"
            >
              Start free trial
              <ArrowRight size={16} />
            </button>
            <Link
              to="/space/rogue/pricing"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/[0.12] text-white font-semibold text-sm hover:border-rogue-cyan/50 hover:text-rogue-cyan transition-all duration-250"
            >
              View pricing
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
